import axios from 'axios'
import store from '../store'
import Vue from 'vue'
import { AppVersion, OS, OSVersion } from '../deviceDetails'
import { Success } from '@/statusCodes.js'

const appBaseUrl =
	process.env.VUE_APP_BASE_URL || (typeof window !== 'undefined' ? `${window.location.origin}/` : '')
const normalizedAppBaseUrl = appBaseUrl.endsWith('/') ? appBaseUrl : `${appBaseUrl}/`
const baseURL = `${normalizedAppBaseUrl}api/d`;

const axiosInstance = axios.create({
	baseURL,
	headers: {
		OS: 'Windows',
		OSVersion: '13.0',
		AppVersion,
		Platform: 'Web'
	},
	timeout: 11111000 // 30 sec timeout
})

let isRefreshingToken = false
const refreshAndRetryQueue = []

function getResponseStatusCode(payload) {
	return payload?.statusCode ?? payload?.StatusCode
}

function normalizeAuthResponse(payload) {
	const nestedData = payload?.data ?? payload?.Data ?? payload

	return {
		statusCode: getResponseStatusCode(payload) ?? getResponseStatusCode(nestedData),
		token: nestedData?.token ?? nestedData?.Token ?? payload?.token ?? payload?.Token ?? '',
		refreshToken:
			nestedData?.refreshToken ??
			nestedData?.RefreshToken ??
			payload?.refreshToken ??
			payload?.RefreshToken ??
			''
	}
}

function getStoredRefreshToken() {
	return store.getters.getRefreshToken || localStorage.getItem('refreshToken')
}

function getDeviceId() {
	return (
		localStorage.getItem('deviceId') ||
		localStorage.getItem('ip') ||
		store.getters['locationStore/getLocationIpAddress'] ||
		'web_unknown'
	)
}

function persistAuthTokens({ token, refreshToken }) {
	if (token) {
		store.commit('setToken', token)
	}

	if (refreshToken) {
		store.commit('setRefreshToken', refreshToken)
	}
}

function clearSessionAndRedirect() {
	store.commit('setUserBlank')
	store.commit('setPrescriptionList', [])
	store.commit('setProfileImage', null)
	store.commit('CLOSE_DRAWER')
	localStorage.removeItem('isLogin')
	window.location.href = '/'
}

function processRefreshQueue(error, token) {
	refreshAndRetryQueue.forEach(({ config, resolve, reject }) => {
		if (error) {
			reject(error)
			return
		}

		config.headers = config.headers || {}
		config._retry = true
		config.headers.Authorization = token

		axiosInstance
			.request(config)
			.then((response) => resolve(response))
			.catch((err) => reject(err))
	})

	refreshAndRetryQueue.length = 0
}

async function handleUnauthorizedRequest(originalRequest) {
	if (!originalRequest || originalRequest._retry || originalRequest.url?.includes('/RefreshToken')) {
		clearSessionAndRedirect()
		throw new Error('Session expired')
	}

	if (isRefreshingToken) {
		return new Promise((resolve, reject) => {
			refreshAndRetryQueue.push({ config: originalRequest, resolve, reject })
		})
	}

	isRefreshingToken = true
	originalRequest._retry = true

	try {
		const { error: refreshError, data } = await getrefreshToken()

		if (refreshError || !data || data.statusCode !== Success || !data.token) {
			throw refreshError || new Error('Unable to refresh token')
		}

		persistAuthTokens(data)
		processRefreshQueue(undefined, data.token)

		originalRequest.headers = originalRequest.headers || {}
		originalRequest.headers.Authorization = data.token

		return axiosInstance(originalRequest)
	} catch (refreshError) {
		processRefreshQueue(refreshError)
		clearSessionAndRedirect()
		throw refreshError instanceof Error ? refreshError : new Error('Session expired')
	} finally {
		isRefreshingToken = false
	}
}

axiosInstance.interceptors.request.use((config) => {
	if (!config.url.includes('/Login') && !config.url.includes('/RefreshToken')) {
		const requestHeaders = config.headers || {}
		const customScreenName = requestHeaders.ScreenName || requestHeaders.screenname
		const defaultScreenName = localStorage.getItem('isSubUser') === 'true'
			? 'Profile'
			: store.getters.getScreenName
		const userId = store.getters.getUserId

		config.headers = config.headers || {}
		config.headers['Authorization'] = store.getters.getToken;
		if (userId !== null && userId !== undefined && userId !== '') {
			config.headers['UserId'] = userId;
		} else {
			delete config.headers['UserId'];
		}
		config.headers['ScreenName'] = customScreenName || defaultScreenName;
		config.headers['x-api-version'] = '1.0';
		
		const deviceId = getDeviceId()
		config.headers['DeviceId'] = deviceId;
	} else if (config.url.includes('/Login')) {
		config.headers = config.headers || {}
		config.headers['ScreenName'] = "Login";
		const deviceId = getDeviceId()
		config.headers['DeviceId'] = deviceId;
	}
	return config;
},
	(error) => {
		console.error('Request interceptor error:', error);
		return Promise.reject(error);
	}
)

axiosInstance.interceptors.response.use(
	async (response) => {
		if (getResponseStatusCode(response.data) === 401) {
			return handleUnauthorizedRequest(response.config)
		}
		return response
	},
	async (error) => {
		const originalRequest = error.config
		
		// **NEW: Handle network errors gracefully**
		if (!error.response) {
			console.error('Network error - no response from server:', error.message);
			// Return a structured error instead of rejecting
			return Promise.reject({
				message: 'Network Error',
				isNetworkError: true,
				originalError: error
			});
		}
		
		if (error?.response?.status === 401 && getResponseStatusCode(error?.response?.data) === 401) {
			return handleUnauthorizedRequest(originalRequest)
		}
		
		// **NEW: Return structured error instead of raw rejection**
		return Promise.reject({
			message: error.response?.data?.message || error.message || 'An error occurred',
			status: error.response?.status,
			data: error.response?.data,
			originalError: error
		});
	}
)

export default () => {
	return axiosInstance
}

// Get feature status from server
export const getFeatureStatus = async () => {
  try {
    const response = await axios.get(`${baseURL}/api/patients/features`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch feature status:', error);
    throw error;
  }
};

// Patient API with conditional ABHA support
export const insertOrUpdatePatient = async (patientData) => {
  try {
    const endpoint = patientData.id 
      ? `${baseURL}/api/patients/update/${patientData.id}`
      : `${baseURL}/api/patients/register`;
    
    const response = await axios({
      method: patientData.id ? 'PUT' : 'POST',
      url: endpoint,
      data: patientData
    });
    
    return { data: response.data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

async function getrefreshToken() {
	const oldToken = store.getters.getToken
	const currentRefreshToken = getStoredRefreshToken()

	if (currentRefreshToken) {
		try {
			const refreshToken = await axios.post(`${baseURL}/RefreshToken`, {}, {
				
				headers: {
					OS,
					OSVersion,
					AppVersion,
					Platform: 'Web',
					UserId: store.getters.getUserId,
					ScreenName: store.getters.getScreenName,
					DeviceId: getDeviceId(),
					Authorization: currentRefreshToken
				}
			})

			const newData = normalizeAuthResponse(refreshToken.data)
			const newToken = newData.token

			console.log("OLD TOKEN:", oldToken)
			console.log("NEW TOKEN:", newToken)

			if (newToken && newToken !== oldToken) {
				console.log("A fresh token was issued")
			} else {
				console.log("Token is the same as before (no rotation)")
			}

			return { data: newData, error: undefined }
		} catch (e) {
			return { data: undefined, error: e }
		}
	} else {
		try {
			const user = store.getters.getUser
			const response = await axios.post(
				`${baseURL}/Login`,
				{
					username: Vue.CryptoJS.AES.decrypt(user.userName, Vue.prototype.$secretKey).toString(Vue.CryptoJS.enc.Utf8),
					password: Vue.CryptoJS.AES.decrypt(user.password, Vue.prototype.$secretKey).toString(Vue.CryptoJS.enc.Utf8),
					city: store.getters['locationStore/getLocationCity'] ?? 'unknown',
					state: store.getters['locationStore/getLocationState'] ?? 'unknown',
					country: store.getters['locationStore/getLocationCountry'] ?? 'unknown',
				},
				{
					headers: {
						OS,
						OSVersion,
						AppVersion,
						Platform: 'Web',
						ScreenName: store.getters.getScreenName,
						DeviceId: getDeviceId()
					}
				}
			)

			const newData = normalizeAuthResponse(response.data)
			const newToken = newData.token

			console.log("OLD TOKEN:", oldToken)
			console.log("NEW TOKEN (from login):", newToken)

			if (newToken && newToken !== oldToken) {
				console.log("New login token is different")
			} else {
				console.log("Login returned the same token")
			}

			return { data: newData, error: undefined }
		} catch (e) {
			return { data: undefined, error: e }
		}
	}
}
